import * as React from 'react'

import { css, mq, spacing, styled, Text } from '@thg-commerce/gravity-theme'

export interface FeatureRatingRectanglesProps {
  featureRatingState: number
  displayOnly?: boolean
  featureRatingType?: string
  setFeatureRatingState?: React.Dispatch<React.SetStateAction<string>>
  setError?: React.Dispatch<React.SetStateAction<string | boolean>>
  className?: string
  measurementLabels?: string[]
  featureRatingName?: string
  maxRectangles?: number
}

const FeatureRatingContainer = styled.div<{
  displayOnly?: boolean
  featureRatingType?: string
}>`
  width: 100%;
  margin-bottom: ${(props) =>
    props.displayOnly
      ? props.featureRatingType === 'scale'
        ? spacing(1)
        : spacing(3)
      : spacing(2)};
`

const FeatureRatingRectanglesContainer = styled.div<{
  featureRatingType?: string
  displayOnly?: boolean
}>`
  display: flex;
  align-content: center;
  flex-direction: row;
  width: 100%;
  max-width: ${(props) => (props.displayOnly ? '100%' : '344px')};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    max-width: ${(props) => (props.displayOnly ? '242px' : '344px')};
  }

  ${(props) => props.displayOnly && `border: 0;`}

  ${(props) =>
    props.featureRatingType === 'scale' &&
    !props.displayOnly &&
    `:hover {
  button {
  background: ${props.theme.colors.palette.brand.lighter};
  }
  }
  button:hover ~ button {
    background: ${props.theme.colors.palette.greys.lighter};
  }
  :focus-within {
    button {
      background: ${props.theme.colors.palette.brand.lighter};
    }
    button:focus ~ button {
      background: ${props.theme.colors.palette.greys.lighter};;
    }
  } 
  
  button:focus-visible ~ button {
    background: ${props.theme.colors.palette.greys.lighter};;
  }
`}
`

const Rectangle = styled.button<{
  selected: boolean
  index: number
  displayOnly?: boolean
}>`
  width: 20%;
  height: 16px;
  white-space: ${(props) => (props.displayOnly ? 'nowrap' : 'initial')};
  border-top: 1px solid ${(props) => props.theme.colors.palette.greys.grey};
  border-bottom: 1px solid ${(props) => props.theme.colors.palette.greys.grey};
  background: ${(props) =>
    props.selected
      ? props.theme.colors.palette.brand.base
      : props.theme.colors.palette.greys.light};

  ${(props) =>
    props.displayOnly &&
    css`
      border: 0;

      &:last-of-type {
        direction: rtl;
      }

      ${props.selected &&
      css`
        background: ${props.theme.colors.palette.greys.darker};
      `}
    `}
  ${(props) =>
    !props.displayOnly &&
    css`
      border-left: 1px solid ${(props) => props.theme.colors.palette.greys.grey};

      &:first-child {
        border-left: 1px solid
          ${(props) => props.theme.colors.palette.greys.grey};
      }

      &:last-child {
        border-right: 1px solid
          ${(props) => props.theme.colors.palette.greys.grey};
      }

      &:hover,
      &:focus-visible {
        background: ${(props) => props.theme.colors.palette.brand.lighter};
      }

      &:focus-visible {
        outline: solid 2px ${(props) => props.theme.colors.palette.brand.base};
        z-index: 2;
      }
    `}
  p {
    padding-top: ${spacing(2)};
    ${Text('small', 'default')}
    ${(props) => {
      switch (props.index) {
        case 0:
          return 'text-align:left;'
        case 4:
          return 'text-align:right;'
        default:
          return 'text-align:center;'
      }
    }};
  }
`

const StyledLabelText = styled.p`
  ${Text('small', 'alternate')}
  margin-bottom: ${spacing(1)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    span {
      display: none;
    }
  }
`

const RectangleLabel = styled.span<{
  showOnlyOnDesktop?: boolean
}>`
  ${Text('small', 'default')}
  line-height: 16px;
  width: 50px;
  margin-left: 0;

  display: ${(props) => (props.showOnlyOnDesktop ? 'none' : 'inline-block')};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    display: ${(props) => (!props.showOnlyOnDesktop ? 'none' : 'inline-block')};
    width: 90px;
    margin-left: ${spacing(2)};
    line-height: 16px;
  }
`

export const FeatureRatingRectangles = (
  props: FeatureRatingRectanglesProps,
) => {
  const featureRatingTexts = props.measurementLabels || []
  const rectanglesLength = props.maxRectangles || 5

  const rectangleLabel =
    props.featureRatingType === 'scale'
      ? `${props.featureRatingState}/${rectanglesLength}`
      : featureRatingTexts[props.featureRatingState - 1]

  const rectangles = Array.from({ length: rectanglesLength }, (_, index) => {
    const selected =
      props.featureRatingType === 'scale'
        ? !!(props.featureRatingState && index + 1 <= props.featureRatingState)
        : !!(props.featureRatingState && index + 1 === props.featureRatingState)

    const featureRatingScaleText = !props.displayOnly && (
      <p>
        {index + 1} - {featureRatingTexts[index]}
      </p>
    )
    const featureRatingMeasurementText =
      props.displayOnly && (index === 0 || index === 4) ? (
        <p>{featureRatingTexts[index]}</p>
      ) : (
        !props.displayOnly && <p>{featureRatingTexts[index]}</p>
      )

    const underRectangleLabels =
      (index === 0 || index === 4 || index === props.featureRatingState - 1) &&
      (props.featureRatingType === 'scale'
        ? featureRatingScaleText
        : featureRatingMeasurementText)

    return (
      <Rectangle
        type={'button'}
        key={index}
        index={index}
        disabled={props.displayOnly}
        onClick={() => {
          if (props.setFeatureRatingState) {
            props.setFeatureRatingState(String(index + 1))
          }
          if (props.setError) {
            props.setError(false)
          }
        }}
        data-testid={`rating-bar-${index}`}
        selected={selected}
        displayOnly={props.displayOnly}
      >
        {underRectangleLabels}
      </Rectangle>
    )
  })

  const isScale = (): boolean => props.featureRatingType === 'scale'

  return (
    <FeatureRatingContainer
      className={props.className}
      displayOnly={props.displayOnly}
      featureRatingType={props.featureRatingType}
    >
      {props.displayOnly && (
        <StyledLabelText>
          {props.featureRatingName}
          <span>: </span>
          <RectangleLabel>{rectangleLabel}</RectangleLabel>
        </StyledLabelText>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <FeatureRatingRectanglesContainer
          featureRatingType={props.featureRatingType}
          displayOnly={props.displayOnly}
          aria-hidden
        >
          {rectangles}
        </FeatureRatingRectanglesContainer>
        {props.displayOnly && isScale() && (
          <RectangleLabel showOnlyOnDesktop>{rectangleLabel}</RectangleLabel>
        )}
      </div>
    </FeatureRatingContainer>
  )
}
